import { BasePaginator } from './base/base-model';

/**
 * Selects the workspace experience and worker capability used for execution.
 * Numeric values mirror the persisted C# enum; never reorder or renumber them independently.
 */
export enum GlobalConsoleMode {
  /** Registered device terminal using platform-managed connection details. */
  Device = 0,
  /** An isolated Ansible playbook, inventory, and variables workspace. */
  Ansible = 1,
  /** An isolated Terraform configuration and state workspace. */
  Terraform = 2,
  /** A governed interactive SSH client with no preselected managed device. */
  FreeSsh = 3
}

/**
 * Shared workspace/session/execution lifecycle. Ready through Running are active states;
 * Succeeded through Ended are completion or connectivity outcomes. Values are API contracts.
 */
export enum GlobalConsoleStatus {
  /** Created and able to accept work. */
  Ready = 0,
  /** Dispatched and waiting for an eligible worker to claim the task. */
  Queued = 1,
  /** Worker is establishing the target connection. */
  Connecting = 2,
  /** Worker is processing or maintaining an interactive command. */
  Running = 3,
  /** Command completed successfully. */
  Succeeded = 4,
  /** Command completed with an error. */
  Failed = 5,
  /** Execution exceeded its configured deadline. */
  TimedOut = 6,
  /** Execution was cancelled by the user or platform. */
  Cancelled = 7,
  /** An established worker or target connection was lost. */
  Disconnected = 8,
  /** Session/workspace was intentionally closed and accepts no more work. */
  Ended = 9
}

/** Short-lived step-up challenge; verification secrets are never returned to the browser. */
export interface GlobalConsoleAccessChallenge {
  Id: string;
  Provider: 'LocalSplitEmailSms' | 'MicrosoftEntra';
  ExpiresAt: string;
  Grant?: string;
  AudienceId?: string;
  GrantExpiresAt?: string;
  RiskAcknowledgementVersion?: string;
  MaskedEmail?: string;
  MaskedPhone?: string;
  TenantId?: string;
  ClientId?: string;
  LoginHint?: string;
  AuthenticationContext?: string;
}

export interface GlobalConsoleAccessGrant {
  Grant: string;
  AudienceId: string;
  GrantExpiresAt: string;
  RiskAcknowledgementVersion: string;
}

/** Top-level user-owned container displayed in the server-paginated history list. */
export interface GlobalConsoleWorkspace {
  Id: string;
  Name: string;
  Mode: GlobalConsoleMode;
  Status: GlobalConsoleStatus;
  CorrelationId: string;
  LastActivityAt: string;
}

export type GlobalConsoleWorkspacePage = BasePaginator<GlobalConsoleWorkspace>;

/** One terminal tab with an independent target, worker affinity, and output sequence. */
export interface GlobalConsoleSession {
  Id: string;
  WorkspaceId: string;
  SessionGroupId: string;
  Name: string;
  Mode: GlobalConsoleMode;
  Status: GlobalConsoleStatus;
  SelectedDeviceId?: string;
  DeviceName?: string;
  DeviceIp?: string;
  LastSequence: number;
  ProtectedInputActive?: boolean;
}

/** Versioned Ansible/Terraform file; Sensitive controls handling, not authorization by itself. */
export interface GlobalConsoleFile {
  Id: string;
  WorkspaceId: string;
  Name: string;
  Content: string;
  Version: number;
  Sensitive: boolean;
  SourceType?: string;
  SourceId?: string;
  SourceVersion?: string;
}

/**
 * Metadata for one submitted command. Transcript events are live response data; the backend's
 * metadata-only retention policy does not persist their Text values.
 */
export interface GlobalConsoleExecution {
  Id: string;
  WorkspaceId: string;
  SessionGroupId: string;
  SessionId: string;
  ParentExecutionId?: string;
  Command: string;
  Operation: string;
  Status: GlobalConsoleStatus;
  Transcript: Array<{ Sequence: number; Stream: string; Text: string; ProtectedInput: boolean }>;
}

/** Ordered, live-only terminal event delivered to the current console audience over SignalR. */
export interface GlobalConsoleLiveEvent {
  WorkspaceId: string;
  SessionId: string;
  ExecutionId?: string;
  Sequence: number;
  Status: GlobalConsoleStatus;
  SessionStatus?: GlobalConsoleStatus;
  Stream?: string;
  Text?: string;
  ProtectedInput?: boolean;
  InputProtected?: boolean;
  CompletedAt?: string;
}

/** Complete authorized workspace state used when opening or restoring the console. */
export interface GlobalConsoleSnapshot {
  Workspace: GlobalConsoleWorkspace;
  Sessions: GlobalConsoleSession[];
  Files: GlobalConsoleFile[];
  Executions: GlobalConsoleExecution[];
}
